import React, { type ReactElement, useMemo } from 'react';
import { type ColumnDef, createColumnHelper } from '@tanstack/react-table';
import TableCheckbox from 'atom-components/table-checkbox';
import ClientControlledTable from 'atom-components/tables/client-controlled-table';
import type { RowSelectionProps } from 'atom-components/tables/types';
import TooltipItem from 'atom-components/tooltip-item';
import { joinWithConjunction } from 'helpers/string.helpers';
import ActiveMarkerBadge from 'pages/marking/components/active-marker-badge';
import { markStatusDisplayName } from 'pages/marking/helpers/presentation.helpers';
import type { AllocateResponsesRow } from 'pages/marking/subpages/marking-allocation-tab/types';
import type { BaseCustomerUser } from 'services/assist-api/entities/customer-user/types';
import { useMe } from 'services/assist-api/entities/me/me';
import { MarkStatus } from 'services/assist-api/entities/test-instance-response-mark/types';
import type { BaseMarkingTask } from 'services/assist-api/entities/test-instance-response-marker/marking-tasks/types';
import {
  getOrganisationUserMarkingTasks,
  isAlreadyAllocatedToAllResponses,
  markingTasksByStatusCount
} from './allocate-responses.helpers';

type ResponseCountHeaderProps = {
  heading: string;
  markStatuses: MarkStatus[];
};

function ResponsesCountHeader({ heading, markStatuses }: ResponseCountHeaderProps): ReactElement {
  const friendlyMarkStatuses = markStatuses.map((s) => markStatusDisplayName(s));
  return (
    <TooltipItem tooltip={`Mark Status: ${joinWithConjunction(friendlyMarkStatuses, 'or')}`}>
      <span>{heading}</span>
    </TooltipItem>
  );
}

const marksStartedStatuses = [
  MarkStatus.InProgress,
  MarkStatus.NeedsReview,
  MarkStatus.InReview,
  MarkStatus.ReturnedForMarking,
  MarkStatus.FurtherEscalated,
  MarkStatus.Reassigned
];
const marksFinishedStatuses = [MarkStatus.FinishedSuccess, MarkStatus.FinishedFailure];

type AllocateResponsesTableProps = {
  selectedResponseIds: number[];
  organisationUsers: BaseCustomerUser[];
  markingTasks: BaseMarkingTask[];
} & RowSelectionProps;

export default function AllocateResponsesTable({
  selectedResponseIds,
  organisationUsers,
  markingTasks,
  rowSelection,
  setRowSelection
}: AllocateResponsesTableProps): ReactElement {
  // Get the current user.
  const meQuery = useMe();
  const currentOrganisationUser = meQuery.data?.customerUser;

  const columnHelper = createColumnHelper<AllocateResponsesRow>();
  const columns: ColumnDef<AllocateResponsesRow>[] = useMemo(
    () => [
      columnHelper.accessor('id', {
        header: ({ table }) => (
          <TableCheckbox.HeaderInput id="organisation-users-select-all" table={table} />
        ),
        enableSorting: false,
        cell: ({ row }) => {
          const organisationUserMarkingTasks = getOrganisationUserMarkingTasks(
            row.original.organisationUser.id,
            markingTasks
          );
          const isDisabled = isAlreadyAllocatedToAllResponses(
            organisationUserMarkingTasks,
            selectedResponseIds
          );
          const checkboxInput = (
            <TableCheckbox.RowInput
              id={`organisation-users-${row.original.id}`}
              row={row}
              disabled={isDisabled}
            />
          );
          return (
            <>
              {isDisabled ? (
                <TooltipItem tooltip="Already assigned to all selected responses">
                  {checkboxInput}
                </TooltipItem>
              ) : (
                checkboxInput
              )}
            </>
          );
        },
        size: 20
      }),
      columnHelper.accessor('organisationUser.id', {
        header: () => 'User',
        cell: ({ row }) => {
          const displayWarning =
            row.original.organisationUser &&
            currentOrganisationUser &&
            currentOrganisationUser.id !== row.original.organisationUser.id;

          return (
            <TableCheckbox.RowLabel
              htmlFor={`organisation-users-${row.original.organisationUser.id}`}
            >
              <TooltipItem tooltip={`@${row.original.organisationUser.user.username}`}>
                <ActiveMarkerBadge
                  displayWarning={displayWarning ?? false}
                  activeMarker={undefined}
                  defaultActiveMarkerToOrgUser
                  organisationUser={row.original.organisationUser}
                  isAiMarked={undefined}
                />
              </TooltipItem>
            </TableCheckbox.RowLabel>
          );
        }
      }),
      columnHelper.accessor('responsesAssigned', {
        header: () => (
          <ResponsesCountHeader heading="Responses Assigned" markStatuses={[MarkStatus.Assigned]} />
        )
      }),
      columnHelper.accessor('marksStarted', {
        header: () => (
          <ResponsesCountHeader heading="Marks Started" markStatuses={marksStartedStatuses} />
        )
      }),
      columnHelper.accessor('marksFinished', {
        header: () => (
          <ResponsesCountHeader heading="Marks Finished" markStatuses={marksFinishedStatuses} />
        )
      })
    ],
    [columnHelper, currentOrganisationUser, markingTasks, selectedResponseIds]
  );

  const data: AllocateResponsesRow[] = useMemo(
    () =>
      organisationUsers.map((organisationUser) => {
        const organisationUserMarkingTasks = getOrganisationUserMarkingTasks(
          organisationUser.id,
          markingTasks
        );
        return {
          id: organisationUser.id,
          organisationUser,
          responsesAssigned: markingTasksByStatusCount(organisationUserMarkingTasks, [
            null, // If the mark status is null it is considered "Assigned".
            MarkStatus.Assigned
          ]),
          marksStarted: markingTasksByStatusCount(
            organisationUserMarkingTasks,
            marksStartedStatuses
          ),
          marksFinished: markingTasksByStatusCount(
            organisationUserMarkingTasks,
            marksFinishedStatuses
          )
        };
      }),
    [organisationUsers, markingTasks]
  );

  return (
    <ClientControlledTable
      columns={columns}
      data={data}
      initialSorting={[{ id: 'name', desc: false }]}
      rowSelection={rowSelection}
      setRowSelection={setRowSelection}
      entity="marker"
      hideSuggestion
    />
  );
}
